import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog';
import {
  AlertTriangle,
  Award,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Loader2,
  Package,
  Radio,
  PlaneTakeoff,
  Send,
  Shield,
  ShieldCheck,
  Users,
  Wrench,
  XOctagon,
} from 'lucide-react';
import {
  useDispatchPreflight,
  useDispatch,
  groupChecksByCategory,
  CHECK_CATEGORY_META,
  computeProjectReadiness,
  READINESS_META,
  remediationPath,
  getDispatchErrorMessage,
} from '@/hooks/use-dispatch-preflight';
import type {
  PreflightResponse,
  PreflightProject,
  PreflightCheck,
  CheckCategory,
  ProjectReadinessStatus,
  DispatchResult,
} from '@/hooks/use-dispatch-preflight';
import type { BoardTerminology } from '@/hooks/use-staffing-board';
import { DEFAULT_TERMINOLOGY } from '@/hooks/use-staffing-board';
import { humanizeEnum } from '@/lib/labels';

// ─── Sub-components ──────────────────────────────────────────────────────────

const CATEGORY_ICON: Record<string, React.ReactNode> = {
  shield: <Shield className="h-3.5 w-3.5" />,
  award: <Award className="h-3.5 w-3.5" />,
  users: <Users className="h-3.5 w-3.5" />,
  wrench: <Wrench className="h-3.5 w-3.5" />,
  package: <Package className="h-3.5 w-3.5" />,
  radio: <Radio className="h-3.5 w-3.5" />,
};

const OVERRIDE_LABELS: Record<CheckCategory, string> = {
  safety: 'Safety',
  certification: 'Certification',
  crew: 'Crew',
  gear_inspection: 'Gear',
  gear_request: 'Gear',
  communications: 'Communications',
  project_status: 'Project status',
};

const OVERRIDE_ORDER: Record<CheckCategory, number> = {
  safety: 0,
  crew: 1,
  certification: 2,
  gear_inspection: 3,
  gear_request: 4,
  communications: 5,
  project_status: 6,
};

const OVERRIDE_PLACEHOLDERS: Record<CheckCategory, string> = {
  safety:
    'e.g., Safety exception approved — residual risk signed off by site supervisor.',
  certification:
    'e.g., Safety lead approved override — recertification scheduled for next week.',
  crew:
    'e.g., Crewing exception approved — travel/logistics reviewed by ops lead.',
  gear_inspection:
    'e.g., Gear inspection waiver approved — replacement kit reserved for site.',
  gear_request:
    'e.g., Gear request exception approved — missing kit arranged before arrival.',
  communications:
    'e.g., Comms exception approved — alternate contact method confirmed with crew.',
  project_status:
    'e.g., Project activation confirmed with the project manager.',
};

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function joinOverrideCategories(categories: Set<CheckCategory>): string {
  const names = [...categories]
    .sort((a, b) => OVERRIDE_ORDER[a] - OVERRIDE_ORDER[b])
    .map((c) => OVERRIDE_LABELS[c])
    .filter((name, i, arr) => arr.indexOf(name) === i);
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} & ${names[1]}`;
  return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
}

function placeholderForCategories(categories: Set<CheckCategory>): string {
  const sorted = [...categories].sort(
    (a, b) => OVERRIDE_ORDER[a] - OVERRIDE_ORDER[b],
  );
  if (sorted.length === 1) {
    return OVERRIDE_PLACEHOLDERS[sorted[0]];
  }
  const names = sorted.map((c) => OVERRIDE_LABELS[c].toLowerCase());
  const unique = [...new Set(names)];
  if (unique.length === 1) {
    return `e.g., ${capitalize(unique[0])} exceptions approved — items reviewed and cleared for dispatch.`;
  }
  const joined =
    unique.length === 2
      ? `${unique[0]} and ${unique[1]}`
      : `${unique.slice(0, -1).join(', ')} and ${unique[unique.length - 1]}`;
  return `e.g., Safety lead reviewed and approved exceptions for ${joined}.`;
}

function contextForCategories(
  categories: Set<CheckCategory>,
  type: 'blockers' | 'warnings',
): string {
  const names = [...categories]
    .sort((a, b) => OVERRIDE_ORDER[a] - OVERRIDE_ORDER[b])
    .map((c) => OVERRIDE_LABELS[c].toLowerCase());
  const unique = [...new Set(names)];
  if (unique.length === 0) return type;
  const joined =
    unique.length === 1
      ? unique[0]
      : unique.length === 2
        ? `${unique[0]} and ${unique[1]}`
        : `${unique.slice(0, -1).join(', ')} and ${unique[unique.length - 1]}`;
  return `${joined} ${type === 'blockers' ? 'blockers' : 'warnings'}`;
}

function CheckBadge({
  level,
  category,
  checkKey,
}: {
  level: PreflightCheck['level'];
  category?: CheckCategory;
  checkKey?: string;
}) {
  if (level === 'blocker') {
    return <XOctagon className="h-4 w-4 flex-shrink-0 text-red-600" />;
  }
  if (level === 'warning') {
    if (category === 'crew' && checkKey === 'travel_not_confirmed') {
      return <PlaneTakeoff className="h-4 w-4 flex-shrink-0 text-amber-500" />;
    }
    if (category === 'gear_request') {
      return <Package className="h-4 w-4 flex-shrink-0 text-amber-500" />;
    }
    if (category === 'gear_inspection') {
      return <Wrench className="h-4 w-4 flex-shrink-0 text-amber-500" />;
    }
    return <AlertTriangle className="h-4 w-4 flex-shrink-0 text-amber-500" />;
  }
  if (category === 'gear_request') {
    return <Package className="h-4 w-4 flex-shrink-0 text-green-600" />;
  }
  if (category === 'gear_inspection') {
    return <Wrench className="h-4 w-4 flex-shrink-0 text-green-600" />;
  }
  return <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-green-600" />;
}

function ReadinessBadge({ status }: { status: ProjectReadinessStatus }) {
  const meta = READINESS_META[status];
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${meta.bgClass} ${meta.textClass} ${meta.ringClass}`}
      data-testid="readiness-badge"
      data-status={status}
    >
      {status === 'ready' && <CheckCircle2 className="h-3 w-3" />}
      {status === 'warning' && <AlertTriangle className="h-3 w-3" />}
      {status === 'blocked' && <XOctagon className="h-3 w-3" />}
      {status === 'dispatched' && <Send className="h-3 w-3" />}
      {meta.label}
    </span>
  );
}

function railColor(project: PreflightProject): string {
  const hasBlocker = project.checks.some((c) => c.level === 'blocker');
  if (hasBlocker) return 'border-l-red-500 bg-red-50/40';
  const hasWarning = project.checks.some((c) => c.level === 'warning');
  if (hasWarning) return 'border-l-amber-400 bg-amber-50/40';
  return 'border-l-green-500 bg-green-50/40';
}

function ProjectRow({ project }: { project: PreflightProject }) {
  const [expanded, setExpanded] = useState(false);
  const blockers = project.checks.filter((c) => c.level === 'blocker');
  const warnings = project.checks.filter((c) => c.level === 'warning');
  const grouped = groupChecksByCategory(project.checks);
  const readiness = computeProjectReadiness(project);

  return (
    <div
      className={`border-l-4 rounded-lg border border-gray-200 ${railColor(project)}`}
      data-testid="preflight-project-row"
    >
      <button
        type="button"
        className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm"
        onClick={() => setExpanded((e) => !e)}
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4 flex-shrink-0 text-gray-500" />
        ) : (
          <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-500" />
        )}
        <span className="font-medium text-gray-900 flex-1 truncate">
          {project.project_name}
        </span>
        <ReadinessBadge status={readiness} />
        <span className="text-xs text-gray-500">
          {project.tech_count} tech{project.tech_count !== 1 ? 's' : ''}
        </span>
        {blockers.length > 0 && (
          <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
            <XOctagon className="h-3 w-3" />
            {blockers.length}
          </span>
        )}
        {warnings.length > 0 && (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
            <AlertTriangle className="h-3 w-3" />
            {warnings.length}
          </span>
        )}
      </button>

      {project.checks.filter((c) => c.key === 'project_draft').map((c) => (
        <div
          key={c.key}
          className="mx-3 mb-2.5 flex items-start gap-2 rounded-md bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800"
          data-testid="preflight-draft-warning"
        >
          <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
          <span>{c.message}</span>
        </div>
      ))}

      {expanded && (
        <div className="border-t border-gray-200 px-4 py-2 space-y-3">
          {grouped.map(([category, checks, label]) => {
            const catMeta = CHECK_CATEGORY_META[category];
            const catIcon = CATEGORY_ICON[catMeta?.icon ?? ''];
            const hasProblem = checks.some((c) => c.level !== 'ok');
            return (
              <div key={label} data-testid={`preflight-category-${category}`}>
                <div className="flex items-center gap-1.5 mb-1">
                  {catIcon && <span className={hasProblem ? 'text-amber-500' : 'text-gray-400'}>{catIcon}</span>}
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    {label}
                  </p>
                </div>
                <div className="space-y-1">
                  {checks.map((check, i) => {
                    const href = check.level !== 'ok'
                      ? remediationPath(check, project.project_id)
                      : null;
                    return (
                      <div key={i} className="flex items-start gap-2 text-sm">
                        <CheckBadge level={check.level} category={check.category} checkKey={check.key} />
                        <span className="text-gray-700 flex-1">{check.message}</span>
                        {href && (
                          <a
                            href={href}
                            className="inline-flex items-center gap-0.5 text-xs text-blue-600 hover:text-blue-800 whitespace-nowrap"
                            onClick={(e) => e.stopPropagation()}
                            data-testid="remediation-link"
                          >
                            Fix
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {project.techs.length > 0 && (
            <div className="pt-2 border-t border-gray-100">
              <div className="flex items-center gap-1.5 mb-1">
                <Radio className="h-3.5 w-3.5 text-gray-400" />
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Communications</p>
              </div>
              {project.techs.map((tech) => (
                <div key={tech.tech_id} className="flex items-center gap-2 text-sm py-0.5">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    tech.reachable ? 'bg-green-500' : 'bg-gray-300'
                  }`} />
                  <a
                    href={`/crew/techs/${tech.tech_id}`}
                    className="text-gray-800 hover:text-blue-600 hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {tech.person_name}
                  </a>
                  <span className="text-xs text-gray-400">via {tech.channel}</span>
                  {tech.blockers && tech.blockers.length > 0 && (
                    <span className="text-xs text-red-600">
                      ({tech.blockers.map(humanizeEnum).join(', ')})
                    </span>
                  )}
                  {!tech.reachable && (
                    <a
                      href={`/crew/techs/${tech.tech_id}`}
                      className="inline-flex items-center gap-0.5 text-xs text-blue-600 hover:text-blue-800 ml-auto whitespace-nowrap"
                      onClick={(e) => e.stopPropagation()}
                      data-testid="remediation-link"
                    >
                      Fix
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main dialog ─────────────────────────────────────────────────────────────

type DialogPhase = 'preflight' | 'confirm' | 'done';

interface DispatchDialogProps {
  open: boolean;
  onClose: () => void;
  onDispatched?: (count: number, projectCount: number) => void;
  onViewHistory?: () => void;
  terminology?: BoardTerminology;
  assignmentIds?: number[];
}

export function DispatchDialog({
  open,
  onClose,
  onDispatched,
  onViewHistory,
  terminology = DEFAULT_TERMINOLOGY,
  assignmentIds,
}: DispatchDialogProps) {
  const projectNoun = terminology.project_label.toLowerCase();
  const projectNounPlural = terminology.project_label_plural.toLowerCase();
  const [preflight, setPreflight] = useState<PreflightResponse | null>(null);
  const [phase, setPhase] = useState<DialogPhase>('preflight');
  const [dispatchResult, setDispatchResult] = useState<{ dispatched: number; projects: number; errors: DispatchResult['errors'] } | null>(null);
  const [overrideReason, setOverrideReason] = useState('');
  const [dispatchError, setDispatchError] = useState<{ error: string; projects: string[] }[] | null>(null);

  const preflightMutation = useDispatchPreflight();
  const dispatchMutation = useDispatch();

  // Run preflight when dialog opens
  useEffect(() => {
    if (open && !preflight && !preflightMutation.isPending) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPreflight(null);
      setPhase('preflight');
      setDispatchResult(null);
      setDispatchError(null);
      preflightMutation.mutate({ assignment_ids: assignmentIds }, {
        onSuccess: (data) => setPreflight(data),
      });
    }
    if (!open) {
      setPreflight(null);
      setPhase('preflight');
      setDispatchResult(null);
      setOverrideReason('');
      setDispatchError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleProceedToConfirm = useCallback(() => {
    setPhase('confirm');
  }, []);

  const handleBackToPreflight = useCallback(() => {
    setPhase('preflight');
  }, []);

  const handleDispatch = useCallback(() => {
    if (!preflight) return;
    const payload: { confirmation_token: string; override_reason?: string } = {
      confirmation_token: preflight.confirmation_token,
    };
    if (overrideReason.trim()) {
      payload.override_reason = overrideReason.trim();
    }
    dispatchMutation.mutate(
      payload,
      {
        onError: (err) => {
          setDispatchError([
            { error: getDispatchErrorMessage(err, 'Dispatch failed. Please try again.'), projects: [] },
          ]);
        },
        onSuccess: (result) => {
          if (result.dispatched === 0 && result.errors.length > 0) {
            // Nothing was dispatched; stay on the confirm screen and surface
            // grouped error message(s) once per distinct message, with any
            // affected project names listed.
            const grouped = new Map<string, string[]>();
            for (const e of result.errors) {
              const name = e.project_name?.trim() || `Project ${e.project_id}`;
              const list = grouped.get(e.error) ?? [];
              if (!list.includes(name)) list.push(name);
              grouped.set(e.error, list);
            }
            setDispatchError(Array.from(grouped.entries()).map(([error, projects]) => ({ error, projects })));
            return;
          }
          setPhase('done');
          setDispatchResult({
            dispatched: result.dispatched,
            projects: result.projects,
            errors: result.errors,
          });
          onDispatched?.(result.dispatched, result.projects);
        },
      },
    );
  }, [preflight, dispatchMutation, onDispatched, overrideReason]);

  const dispatchableProjects = useMemo(
    () => preflight?.projects.filter((p) => !p.is_foundation) ?? [],
    [preflight],
  );
  const dispatchableChecks = useMemo(
    // The "still Draft" notice is informational: it never requires an
    // acknowledgment reason (warn, never block).
    () => dispatchableProjects.flatMap((p) => p.checks).filter((c) => c.key !== 'project_draft'),
    [dispatchableProjects],
  );
  const hasBlockers = dispatchableChecks.some((c) => c.level === 'blocker');
  const hasWarnings = dispatchableChecks.some((c) => c.level === 'warning');

  const blockerCategories = useMemo(() => {
    const cats = new Set<CheckCategory>();
    for (const c of dispatchableChecks) {
      if (c.level === 'blocker') cats.add(c.category);
    }
    return cats;
  }, [dispatchableChecks]);

  const warningCategories = useMemo(() => {
    const cats = new Set<CheckCategory>();
    for (const c of dispatchableChecks) {
      if (c.level === 'warning') cats.add(c.category);
    }
    return cats;
  }, [dispatchableChecks]);

  const needsOverrideReason = hasBlockers || hasWarnings;

  const overrideLabel = useMemo(() => {
    if (blockerCategories.size > 0) {
      return `${joinOverrideCategories(blockerCategories)} override reason (required)`;
    }
    if (warningCategories.size > 0) {
      return `${joinOverrideCategories(warningCategories)} acknowledgment reason (required)`;
    }
    return 'Acknowledgment reason (required)';
  }, [blockerCategories, warningCategories]);

  const overridePlaceholder = useMemo(() => {
    if (blockerCategories.size > 0) {
      return placeholderForCategories(blockerCategories);
    }
    if (warningCategories.size > 0) {
      return placeholderForCategories(warningCategories);
    }
    return 'e.g., Warnings reviewed with site supervisor — proceeding with mitigation plan.';
  }, [blockerCategories, warningCategories]);

  const overrideContext = useMemo(() => {
    if (blockerCategories.size > 0) {
      return contextForCategories(blockerCategories, 'blockers');
    }
    if (warningCategories.size > 0) {
      return contextForCategories(warningCategories, 'warnings');
    }
    return 'warnings';
  }, [blockerCategories, warningCategories]);
  const isLoading = preflightMutation.isPending;
  const isSending = dispatchMutation.isPending;

  const dispatchableTechCount = dispatchableProjects.reduce((sum, p) => sum + p.tech_count, 0);
  const hasOnlyFoundationProjects =
    preflight !== null && preflight.projects.length > 0 && dispatchableProjects.length === 0;

  const dialogTitle = {
    preflight: 'Readiness Check',
    confirm: 'Confirm Dispatch',
    done: 'Dispatch Complete',
  }[phase];

  const dialogDescription = {
    preflight: `Review readiness checks for all assigned ${projectNounPlural}. No notifications will be sent.`,
    confirm: `You are about to dispatch ${dispatchableTechCount} notification(s) across ${dispatchableProjects.length} ${dispatchableProjects.length !== 1 ? projectNounPlural : projectNoun}. This action will send job packets to all assigned technicians.`,
    done: `Successfully dispatched to ${dispatchResult?.dispatched ?? 0} tech(s) across ${dispatchResult?.projects ?? 0} ${(dispatchResult?.projects ?? 0) !== 1 ? projectNounPlural : projectNoun}.`,
  }[phase];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] md:max-h-[80vh] flex flex-col w-[calc(100%-1rem)] sm:w-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {phase === 'preflight' && <ShieldCheck className="h-5 w-5 text-blue-600" />}
            {phase === 'confirm' && <Send className="h-5 w-5 text-amber-600" />}
            {phase === 'done' && <CheckCircle2 className="h-5 w-5 text-green-600" />}
            {dialogTitle}
          </DialogTitle>
          <DialogDescription>
            {dialogDescription}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-2 py-2 min-h-0">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              <span className="ml-2 text-sm text-gray-500">Running preflight checks...</span>
            </div>
          )}

          {preflightMutation.isError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="preflight-error">
              {getDispatchErrorMessage(preflightMutation.error, 'Failed to run preflight checks. Please try again.')}
            </div>
          )}

          {dispatchError && dispatchError.length > 0 && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="dispatch-error">
              <ul className="list-disc ml-4 space-y-1">
                {dispatchError.map(({ error, projects }, i) => (
                  <li key={i}>
                    {error}
                    {projects.length > 0 && (
                      <span className="text-red-600"> — {projects.join(', ')}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {phase === 'done' && dispatchResult && (
            <div className="space-y-3">
              <div className={`rounded-lg border px-4 py-6 ${dispatchResult.dispatched > 0 || dispatchResult.errors.length === 0 ? 'border-green-200 bg-green-50 text-center' : 'border-red-200 bg-red-50 text-left'}`}>
                {dispatchResult.dispatched > 0 || dispatchResult.errors.length === 0 ? (
                  <>
                    <CheckCircle2 className="h-10 w-10 text-green-600 mx-auto mb-2" />
                    <p className="text-lg font-medium text-green-900">
                      {dispatchResult.dispatched} notification{dispatchResult.dispatched !== 1 ? 's' : ''} sent
                    </p>
                    <p className="text-sm text-green-700 mt-1">
                      Across {dispatchResult.projects} {dispatchResult.projects !== 1 ? projectNounPlural : projectNoun}
                    </p>
                  </>
                ) : (
                  <>
                    <XOctagon className="h-10 w-10 text-red-600 mx-auto mb-2" />
                    <p className="text-lg font-medium text-red-900">Dispatch failed</p>
                    <p className="text-sm text-red-700 mt-1">
                      {dispatchResult.errors.map((e) => e.error).join(' ')}
                    </p>
                  </>
                )}
              </div>
              {dispatchResult.errors.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  <p className="font-medium">Some {projectNounPlural} could not be dispatched:</p>
                  <ul className="list-disc ml-5 mt-1 space-y-0.5">
                    {(() => {
                      const grouped = new Map<string, string[]>();
                      for (const e of dispatchResult.errors) {
                        const name = e.project_name?.trim() || `${projectNoun.charAt(0).toUpperCase() + projectNoun.slice(1)} ${e.project_id}`;
                        const list = grouped.get(e.error) ?? [];
                        if (!list.includes(name)) list.push(name);
                        grouped.set(e.error, list);
                      }
                      return Array.from(grouped.entries()).map(([error, projects]) => (
                        <li key={error}>
                          {error}
                          {projects.length > 0 && <span className="text-amber-700"> — {projects.join(', ')}</span>}
                        </li>
                      ));
                    })()}
                  </ul>
                </div>
              )}
            </div>
          )}

          {preflight && phase !== 'done' && (
            <>
              {/* Summary bar */}
              <div className="flex flex-wrap items-center gap-3 px-1 py-1.5 text-sm" data-testid="preflight-summary-bar">
                <span className="text-gray-600">
                  <strong>{preflight.summary.projects}</strong> {preflight.summary.projects !== 1 ? projectNounPlural : projectNoun}
                </span>
                <span className="text-gray-600">
                  <strong>{preflight.summary.techs}</strong> tech{preflight.summary.techs !== 1 ? 's' : ''}
                </span>
                {preflight.summary.blockers > 0 && (
                  <span className="inline-flex items-center gap-1 text-red-700 font-medium">
                    <XOctagon className="h-3.5 w-3.5" />
                    {preflight.summary.blockers} blocker{preflight.summary.blockers !== 1 ? 's' : ''}
                  </span>
                )}
                {preflight.summary.warnings > 0 && (
                  <span className="inline-flex items-center gap-1 text-amber-600">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    {preflight.summary.warnings} warning{preflight.summary.warnings !== 1 ? 's' : ''}
                  </span>
                )}
                {preflight.summary.blockers === 0 && preflight.summary.warnings === 0 && (
                  <span className="inline-flex items-center gap-1 text-green-600 font-medium">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    All clear
                  </span>
                )}
              </div>

              {/* Excluded (closed/cancelled/completed) projects that still have crew */}
              {preflight.excluded_projects && preflight.excluded_projects.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-800 space-y-1" data-testid="preflight-excluded-projects">
                  {preflight.excluded_projects.map((p) => (
                    <div key={p.project_id} className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      <span>{p.message}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Safety advisory */}
              {phase === 'preflight' && (
                <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-2.5 text-xs text-blue-700 flex items-start gap-2" data-testid="safety-advisory">
                  <ShieldCheck className="h-4 w-4 flex-shrink-0 mt-0.5 text-blue-500" />
                  <span>
                    Readiness checks are advisory. A human must review and confirm dispatch.
                    This panel does not grant safety approval.
                  </span>
                </div>
              )}

              {/* Foundation-only advisory */}
              {preflight.projects.some((p) => p.is_foundation) && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-800 flex items-start gap-2" data-testid="foundation-advisory">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5 text-amber-500" />
                  <span>
                    Foundation-only job sites can't be dispatched until they are linked to a project.
                    They will appear in readiness checks but will not receive notifications.
                  </span>
                </div>
              )}

              {/* Confirm-phase warning banner */}
              {phase === 'confirm' && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>
                    Confirming will send job packets and notifications to all assigned technicians.
                    This cannot be undone.
                  </span>
                </div>
              )}

              {/* Override reason — required when warnings or blockers are present */}
              {phase === 'confirm' && needsOverrideReason && (
                <div
                  id="dispatch-override-reason-fields"
                  data-field-container
                  className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 space-y-2"
                  data-testid="override-reason-section"
                >
                  <label htmlFor="override-reason" className="block text-sm font-medium text-gray-700">
                    {overrideLabel}
                  </label>
                  <p className="text-xs text-gray-500">
                    Readiness checks are advisory — the system suggests, the human confirms.
                    Please describe why you are proceeding despite outstanding {overrideContext}.
                  </p>
                  <textarea
                    id="override-reason"
                    value={overrideReason}
                    onChange={(e) => setOverrideReason(e.target.value)}
                    rows={3}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm placeholder:text-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    placeholder={overridePlaceholder}
                    data-testid="override-reason-input"
                    required
                  />
                </div>
              )}

              {/* Project rows */}
              {preflight.projects.map((project) => (
                <ProjectRow key={`${project.is_foundation ? 'f' : 'p'}-${project.project_id}`} project={project} />
              ))}
            </>
          )}
        </div>

        <DialogFooter>
          {phase === 'done' ? (
            <div className="flex items-center gap-2">
              {onViewHistory && (
                <button
                  type="button"
                  onClick={() => { onClose(); onViewHistory(); }}
                  className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2.5 md:py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  View status
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2.5 md:py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                Done
              </button>
            </div>
          ) : phase === 'confirm' ? (
            <>
              <button
                type="button"
                onClick={handleBackToPreflight}
                disabled={isSending}
                className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2.5 md:py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Back
              </button>
              <button
                type="button"
                data-validate-required="true"
                data-validation-scope="dispatch-override-reason-fields"
                onClick={handleDispatch}
                disabled={isSending}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 md:py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    Confirm &amp; Send {dispatchableTechCount} Notification{dispatchableTechCount !== 1 ? 's' : ''}
                  </>
                )}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={onClose}
                disabled={isSending}
                className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2.5 md:py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Close
              </button>
              <button
                type="button"
                onClick={handleProceedToConfirm}
                disabled={isLoading || !preflight || hasOnlyFoundationProjects}
                title={hasOnlyFoundationProjects ? "Foundation-only job sites can't be dispatched until they are linked to a project" : undefined}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 md:py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="h-4 w-4" />
                {hasBlockers ? 'Override & Proceed' : 'Proceed to Dispatch'}
              </button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
